import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { LogoInnovamat, WavesBg } from '@innovamat/glimmer-assets';
import { useQueryParams } from '@innovamat/hooks';
import { getPublicLanguage } from '@innovamat/localization';

import { Button } from '@innovamat/glimmer-components';
import { useLanguage } from '../../hooks/use-language';
import LanguageButton from '../login-register-layout/language-button';

import {
  Background,
  Container,
  DropdownContainer,
  Error,
  FormBody,
  FormContainer,
  FormHeader,
  LogoContainer,
  StyledNotification,
  Title,
} from './two-columns-layout.styled';

type NotificationType =
  | {
      message: string;
      type: 'error' | 'warning';
    }
  | undefined;

type Props = {
  isSuccess?: boolean;
  title?: string;
  children: JSX.Element;
  error?: string | JSX.Element;
  canGoBack?: boolean;
  onSuccessBody?: JSX.Element;
  notification?: NotificationType;
  titleId?: string;
  dropdownId?: string;
  goBackId?: string;
};

const TwoColumnLayout = ({
  isSuccess,
  children,
  title,
  canGoBack,
  onSuccessBody,
  error,
  notification,
  titleId,
  dropdownId,
  goBackId,
}: Props) => {
  const { setLanguageFromUrl, setHtmlLanguage } = useLanguage();
  const { lng: language } = useParams<{ lng: string }>();
  const { t, i18n } = useTranslation();
  const params = useQueryParams();

  const currentLanguage =
    params.get('l') || params.get('language') || language || i18n.language;

  const backPath = params.get('back') || '/';

  const handleSelectLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  useEffect(() => {
    const language = getPublicLanguage(currentLanguage);

    if (language) {
      handleSelectLanguage(language.value);
    } else {
      setLanguageFromUrl();
    }
  }, [currentLanguage]);

  useEffect(() => {
    setHtmlLanguage(i18n.language);
  }, [i18n.language]);

  return (
    <Container>
      <FormContainer>
        <FormHeader>
          <LogoContainer>
            <Link to="/">
              <LogoInnovamat />
            </Link>
          </LogoContainer>
          <DropdownContainer data-testid={dropdownId}>
            <LanguageButton
              language={t(`language.${i18n.language}`)}
              handleLanguageClick={(value) => handleSelectLanguage(value)}
            />
          </DropdownContainer>
        </FormHeader>
        <FormBody>
          {isSuccess && onSuccessBody ? (
            <>
              {canGoBack && backPath !== '/' && (
                <Link to={backPath} className="goBack" data-testid={goBackId}>
                  <Button leftIcon="BackIcon" variant="tertiary">
                    {t('back.button')}
                  </Button>
                </Link>
              )}
              {onSuccessBody}
            </>
          ) : (
            <>
              {notification && (
                <StyledNotification
                  text={notification.message}
                  type={notification.type}
                />
              )}
              {canGoBack && (
                <Link to={backPath} className="goBack" data-testid={goBackId}>
                  <Button leftIcon="BackIcon" variant="tertiary">
                    {t('back.button')}
                  </Button>
                </Link>
              )}

              <Title>
                {title && <h2 data-testid={`${titleId}-title`}>{title}</h2>}
                <Error data-testid={`${titleId}-error`}>{error}</Error>
              </Title>

              {children}
            </>
          )}
        </FormBody>
        <div />
      </FormContainer>
      <Background>
        <WavesBg />
      </Background>
    </Container>
  );
};

export default TwoColumnLayout;
